import { useId, useLayoutEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { REVIEW_COPY } from '@/pages/review/reviewCopy';
import type { ConfirmGate } from '@/pages/review/reviewListModel';

export interface StickyConfirmBarProps {
  /** Items the batch confirms: "Confirm all [n]". */
  count: number;
  /** `blocked-events` disables the button and shows the need-a-date line. */
  gate: ConfirmGate;
  /** A confirm is in flight or retrying. */
  busy: boolean;
  /** Plan limit reached (C8): disabled, no gate line. */
  disabled?: boolean;
  onConfirm(): void;
}

/**
 * Review v0 footer (Review Flow v2.0, "Sticky Bottom Button"). Pinned to the
 * bottom of its scroll container and reserves the home-indicator inset, as
 * every bottom-pinned surface in the app does.
 *
 * The gate line is today's production footer (EmailReviewModal): undated
 * events block Confirm outright. `needs-task-confirmation` does not block —
 * the page asks "Confirm without a date?" on tap.
 */
export function StickyConfirmBar({
  count,
  gate,
  busy,
  disabled = false,
  onConfirm,
}: StickyConfirmBarProps) {
  const blocked = gate.kind === 'blocked-events';
  // Blocked / plan limit: natively disabled. Busy: aria-disabled plus a
  // click guard, so the focused button keeps focus while a confirm runs.
  const nativeDisabled = blocked || disabled;
  const inert = nativeDisabled || busy;
  const label = REVIEW_COPY.confirmAll(count);
  const barRef = useRef<HTMLDivElement>(null);
  const statusId = useId();
  // The live region is always rendered (an empty region that appears with
  // text is not reliably announced); it carries the gate line, or the
  // "confirming" announcement (aria-busy alone is not read out).
  const statusText = blocked
    ? REVIEW_COPY.needDate(gate.count)
    : busy
      ? REVIEW_COPY.confirming(count)
      : '';

  // The bottom toast slot (fixed, z-[400]) would cover this bar, so while
  // the bar is up it publishes its height and the slot sits above it
  // (`--bottom-toast-offset`, read by BottomToastSlot).
  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const root = document.documentElement;
    const publish = () =>
      root.style.setProperty('--bottom-toast-offset', `${bar.offsetHeight}px`);
    publish();
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(publish);
    observer?.observe(bar);
    return () => {
      observer?.disconnect();
      root.style.removeProperty('--bottom-toast-offset');
    };
  }, []);

  return (
    <div
      ref={barRef}
      data-testid="sticky-confirm-bar"
      className="sticky bottom-0 z-[100] flex flex-col gap-2 border-t border-border bg-surface px-5 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]"
    >
      <p
        id={statusId}
        role="status"
        className={blocked ? 'text-center text-sm text-warning' : 'sr-only'}
      >
        {statusText}
      </p>
      <button
        type="button"
        onClick={() => {
          if (inert) return;
          onConfirm();
        }}
        disabled={nativeDisabled}
        aria-disabled={inert}
        aria-busy={busy}
        aria-label={busy ? label : undefined}
        aria-describedby={statusId}
        className="flex min-h-11 w-full cursor-pointer items-center justify-center gap-1.5 rounded-md bg-primary px-4 py-2 text-base font-semibold text-primary-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary aria-disabled:cursor-not-allowed aria-disabled:bg-disabled-bg aria-disabled:text-disabled-text"
      >
        {busy ? (
          <Loader2
            size={16}
            aria-hidden="true"
            className="animate-spin"
          />
        ) : (
          label
        )}
      </button>
    </div>
  );
}
